/**
 * Agentic Networks — selected room uses the same shell as Network Workbench (header + tab strip);
 * the room list lives in the app sidebar only.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { fetchMyAgenticNetworks } from '@/services/agenticNetworksApi';
import AgenticNetworkRoom from './components/AgenticNetworkRoom.jsx';

export default function AgenticNetworkPage() {
  const { activeTenantId } = useAuth();
  const { agenticRooms = [], refreshAgenticNetworks } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [rooms, setRooms] = useState(agenticRooms);

  const networkIdFromUrl = (searchParams.get('networkId') || '').trim();
  const poolIdFromUrl = (searchParams.get('poolId') || '').trim();
  // Optional deep-link to a specific room tab (e.g. the "View N executions" CTA on
  // the Rooms landing → ``&tab=executions``). Forwarded to the room as initialTab.
  const tabFromUrl = (searchParams.get('tab') || '').trim().toLowerCase();

  const loadRooms = useCallback(async () => {
    if (!activeTenantId) {
      setRooms([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const { rooms: r } = await fetchMyAgenticNetworks();
      setRooms(r);
      if (typeof refreshAgenticNetworks === 'function') {
        refreshAgenticNetworks();
      }
    } catch (e) {
      setLoadError(e.message || 'Failed to load rooms');
      setRooms([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, refreshAgenticNetworks]);

  useEffect(() => {
    if (agenticRooms.length) {
      setRooms(agenticRooms);
    }
  }, [agenticRooms]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  const selected = useMemo(() => {
    if (networkIdFromUrl && poolIdFromUrl) {
      const found = rooms.find((x) => x.networkId === networkIdFromUrl && x.poolId === poolIdFromUrl);
      if (found) return found;
      return {
        networkId: networkIdFromUrl,
        poolId: poolIdFromUrl,
        displayName: `${networkIdFromUrl} · ${poolIdFromUrl}`,
      };
    }
    if (rooms.length) {
      return rooms[0];
    }
    return null;
  }, [networkIdFromUrl, poolIdFromUrl, rooms]);

  useEffect(() => {
    if (!selected) return;
    if (networkIdFromUrl === selected.networkId && poolIdFromUrl === selected.poolId) return;
    const next = new URLSearchParams();
    next.set('networkId', selected.networkId);
    next.set('poolId', selected.poolId);
    setSearchParams(next, { replace: true });
  }, [selected?.networkId, selected?.poolId, networkIdFromUrl, poolIdFromUrl, setSearchParams]);

  const roomOptions = useMemo(() => {
    if (!selected) return rooms;
    const has = rooms.some((x) => x.networkId === selected.networkId && x.poolId === selected.poolId);
    if (has) return rooms;
    return [
      ...rooms,
      {
        networkId: selected.networkId,
        poolId: selected.poolId,
        displayName: selected.displayName,
        role: selected.role || '—',
      },
    ];
  }, [rooms, selected]);

  if (!activeTenantId) {
    return (
      <div className="min-h-0 flex-1 overflow-auto bg-gray-50/80 p-6 md:p-10">
        <div className="mx-auto max-w-lg rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Organization workspace required</p>
          <p className="mt-2 text-amber-800">
            Agentic network rooms are scoped to an organization. Switch to a workspace with an org tenant, then
            refresh this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
      <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 md:hidden">
        <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500" htmlFor="agentic-room-mob">
          Room
        </label>
        <select
          id="agentic-room-mob"
          className="mt-1 w-full max-w-md rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm leading-5"
          value={selected ? `${selected.networkId}\t${selected.poolId}` : ''}
          onChange={(e) => {
            const v = e.target.value.split('\t');
            if (v.length === 2) {
              const next = new URLSearchParams(searchParams);
              next.set('networkId', v[0]);
              next.set('poolId', v[1]);
              setSearchParams(next);
            }
          }}
        >
          {roomOptions.length === 0 ? (
            <option value="">No rooms</option>
          ) : (
            roomOptions.map((r) => (
              <option key={`${r.networkId}:${r.poolId}`} value={`${r.networkId}\t${r.poolId}`}>
                {r.displayName || `${r.networkId} · ${r.poolId}`}
              </option>
            ))
          )}
        </select>
      </div>

      {selected ? (
        <AgenticNetworkRoom
          key={`${selected.networkId}:${selected.poolId}`}
          networkId={selected.networkId}
          poolId={selected.poolId}
          displayName={selected.displayName}
          poolRole={selected.role}
          poolRoles={Array.isArray(selected.roles) && selected.roles.length > 0 ? selected.roles : (selected.role ? [selected.role] : [])}
          releaseVersion={selected.releaseVersion}
          lifecycleStatus={selected.lifecycleStatus}
          inactivityWarning={selected.inactivityWarning}
          initialTab={tabFromUrl || undefined}
        />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center p-8 text-center text-gray-500">
          {loading ? (
            <p className="text-sm">Loading…</p>
          ) : loadError ? (
            <p className="text-sm text-red-600">{loadError}</p>
          ) : rooms.length === 0 ? (
            <>
              <p className="text-sm font-medium text-gray-800">No agentic rooms</p>
              <p className="mt-2 max-w-md text-sm text-gray-600">
                When a network is released to Agentic Networks, a room for you will appear in the left sidebar.
              </p>
            </>
          ) : (
            <p className="text-sm">Select a room from the left sidebar</p>
          )}
        </div>
      )}
    </div>
  );
}
